import { formatPersonDisplayName } from './capitalizePersonName.js';
import { effectiveBookingAdvance } from './bookingAdvanceAmounts.js';

export function getBookingParticipantCount(booking) {
  return Math.max(
    Number(booking?.travelerCount) || 0,
    Array.isArray(booking?.travelers) ? booking.travelers.length : 0,
    1
  );
}

function bookingLevelPaidTotal(booking) {
  return (
    Number(booking?.amountPaidOnline ?? booking?.amount_paid_online ?? 0) +
    Number(booking?.amountPaidAtOffice ?? booking?.amount_paid_at_office ?? 0)
  );
}

export function resolveBookingPackage(booking) {
  const trek = booking?.batch?.trek;
  const packages = Array.isArray(trek?.packages) ? trek.packages : [];
  const joinId =
    booking?.joinFromPackageId ??
    booking?.join_from_package_id ??
    booking?.registration?.packageId ??
    booking?.registration?.package?.id;
  if (joinId != null && String(joinId).trim() !== '') {
    const hit = packages.find((p) => String(p.id) === String(joinId));
    if (hit) return hit;
  }
  if (booking?.joinFromPackage) return booking.joinFromPackage;
  if (booking?.registration?.package) return booking.registration.package;
  return packages.find((p) => p.isMain) || packages[0] || null;
}

/** Recompute fee from trek/package when DB totals are zero after an event date change. */
function deriveBookingFeeFromPackages(booking) {
  const trek = booking?.batch?.trek;
  const pkg = resolveBookingPackage(booking);
  if (!trek || !pkg) return 0;

  const basePrice = Number(pkg?.price ?? 0) || 0;
  const travelPackage = booking?.travelPackage ?? booking?.travel_package ?? null;
  const stayOption = booking?.stayOption ?? booking?.stay_option ?? null;

  const travelBusAdjust = Number(pkg?.travelBusAdjust ?? trek?.travelBusAdjust ?? 0);
  const travelTrainAdjust = Number(pkg?.travelTrainAdjust ?? trek?.travelTrainAdjust ?? 0);
  const travelFlightAdjust = Number(pkg?.travelFlightAdjust ?? trek?.travelFlightAdjust ?? 0);
  const stayQuadAdjust = Number(pkg?.stayQuadAdjust ?? trek?.stayQuadAdjust ?? 0);
  const stayTripleAdjust = Number(pkg?.stayTripleAdjust ?? trek?.stayTripleAdjust ?? 0);
  const stayDoubleAdjust = Number(pkg?.stayDoubleAdjust ?? trek?.stayDoubleAdjust ?? 0);

  const travelAdjust =
    travelBusAdjust * (travelPackage === 'bus' ? 1 : 0) +
    travelTrainAdjust * (travelPackage === 'train' ? 1 : 0) +
    travelFlightAdjust * (travelPackage === 'flight' ? 1 : 0);

  const stayOptionsList =
    Array.isArray(trek?.stayOptions) && trek.stayOptions.length > 0 ? trek.stayOptions : null;
  const stayAdjust = stayOptionsList
    ? Number(stayOptionsList.find((o) => String(o.id) === String(stayOption))?.priceAdjust ?? 0) || 0
    : stayQuadAdjust * (stayOption === 'quad' ? 1 : 0) +
      stayTripleAdjust * (stayOption === 'triple' ? 1 : 0) +
      stayDoubleAdjust * (stayOption === 'double' ? 1 : 0);

  const perPerson = basePrice + travelAdjust + stayAdjust;
  const count = getBookingParticipantCount(booking);
  const addOns = Array.isArray(booking?.addOns) ? booking.addOns : booking?.add_ons || [];
  const addOnTotal = addOns.reduce(
    (sum, o) => sum + (Number(o?.price ?? 0) || 0) * (Number(o?.quantity ?? 0) || 0),
    0
  );

  return perPerson * count + addOnTotal;
}

/** Trip fee total — fall back to advance + remaining, then package pricing when stale/zero. */
export function bookingFeeTotal(booking) {
  const total = Number(booking?.totalAmount ?? booking?.total_amount ?? 0);
  if (Number.isFinite(total) && total > 0) return total;
  const advance = Number(booking?.advanceAmount ?? booking?.advance_amount ?? 0);
  const remaining = Number(booking?.remainingAmount ?? booking?.remaining_amount ?? 0);
  const sum = advance + remaining;
  if (Number.isFinite(sum) && sum > 0) return sum;
  return deriveBookingFeeFromPackages(booking);
}

function participantNameAt(booking, traveler, travelerIndex) {
  if (traveler) {
    return formatPersonDisplayName(traveler.firstName, traveler.lastName);
  }
  const travelers = Array.isArray(booking?.travelers) ? booking.travelers : [];
  const hit = travelers[travelerIndex];
  if (hit) return formatPersonDisplayName(hit.firstName, hit.lastName);
  const reg = booking?.registration;
  return formatPersonDisplayName(reg?.firstName, reg?.lastName);
}

function notesMatchParticipant(notes, participantName) {
  const noteText = String(notes || '').toLowerCase();
  const name = String(participantName || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!name) return false;
  if (noteText.includes(`participant: ${name}`) || noteText.includes(`participant:${name}`)) {
    return true;
  }
  // Group online receipts store "Participants: A, B"
  const group = noteText.match(/participants:\s*([^|]+)/i);
  if (group) {
    return group[1]
      .split(',')
      .map((n) => n.trim().replace(/\s+/g, ' '))
      .includes(name);
  }
  return false;
}

function receiptNotesTagParticipant(notes) {
  return /participant\s*:/i.test(String(notes || ''));
}

function participantNamesForBooking(booking, count) {
  const travelers = Array.isArray(booking?.travelers) ? booking.travelers : [];
  return Array.from({ length: count }, (_, i) => {
    const t = travelers[i];
    return participantNameAt(booking, t, i) || `Participant ${i + 1}`;
  });
}

/** Legacy Hasura receipts linked on registration but not yet mirrored in booking_receipts. */
function resolveUnlinkedLegacyReceiptsForPayment(booking) {
  const reg = booking?.registration;
  if (!reg) return [];
  const legacy = Array.isArray(reg.legacyReceipts)
    ? reg.legacyReceipts
    : Array.isArray(reg.receipts)
      ? reg.receipts.filter((r) => r && r.bookingId == null && r.booking_id == null)
      : [];
  const linkedLegacyIds = new Set(
    (Array.isArray(booking?.receipts) ? booking.receipts : [])
      .map((r) => r?.receiptId ?? r?.receipt_id)
      .filter((id) => id != null && String(id).trim() !== '')
  );
  return legacy
    .filter((r) => r && !linkedLegacyIds.has(r.id))
    .map((r) => {
      const paymentId = String(r?.paymentId ?? r?.payment_id ?? '').trim();
      const manual = r?.manualReceiptJson ?? r?.manual_receipt_json;
      const manualNote =
        manual && typeof manual === 'object'
          ? [manual.reference, manual.note, manual.remarks, manual.comment]
              .map((x) => String(x || '').trim())
              .filter(Boolean)
              .join(' | ')
          : typeof manual === 'string'
            ? manual.trim()
            : '';
      const name = String(r?.name ?? '').trim();
      return {
        amount: r?.amount,
        notes: paymentId
          ? `Payment ID: ${paymentId}`
          : manualNote || name || null,
        method: r?.paymentMode ?? r?.payment_mode ?? 'office'
      };
    });
}

function allReceiptRowsForPayment(booking) {
  const bookingReceipts = Array.isArray(booking?.receipts) ? booking.receipts : [];
  return [...bookingReceipts, ...resolveUnlinkedLegacyReceiptsForPayment(booking)];
}

/** Paid amount attributed to each participant index (tagged receipts first, then untagged FIFO). */
export function buildParticipantPaidByIndex(booking) {
  const count = getBookingParticipantCount(booking);
  const share = bookingFeeTotal(booking) / count;
  const names = participantNamesForBooking(booking, count);
  const paidByIndex = new Array(count).fill(0);
  const receipts = allReceiptRowsForPayment(booking);

  for (const receipt of receipts) {
    const amount = Number(receipt?.amount ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) continue;

    let matched = false;
    for (let i = 0; i < count; i += 1) {
      if (notesMatchParticipant(receipt?.notes, names[i])) {
        paidByIndex[i] += amount;
        matched = true;
        break;
      }
    }
    if (matched) continue;
    // Cash/QR receipts tagged for another group member must not apply here.
    if (receiptNotesTagParticipant(receipt?.notes)) continue;

    let remaining = amount;
    for (let i = 0; i < count && remaining > 0; i += 1) {
      const room = Math.max(0, share - paidByIndex[i]);
      const applied = Math.min(remaining, room);
      paidByIndex[i] += applied;
      remaining -= applied;
    }
  }

  const receiptTotal = paidByIndex.reduce((sum, n) => sum + n, 0);
  const bookingPaid = bookingLevelPaidTotal(booking);
  let remainder = Math.max(0, bookingPaid - receiptTotal);
  if (remainder > 0) {
    for (let i = 0; i < count && remainder > 0; i += 1) {
      const room = Math.max(0, share - paidByIndex[i]);
      const applied = Math.min(remainder, room);
      paidByIndex[i] += applied;
      remainder -= applied;
    }
  }

  return { count, share, names, paidByIndex };
}

export function getParticipantPaymentState(booking, traveler, travelerIndex) {
  const count = getBookingParticipantCount(booking);
  const total = bookingFeeTotal(booking);
  const idx = Number.isFinite(travelerIndex) ? travelerIndex : 0;
  const { share, paidByIndex } = buildParticipantPaidByIndex(booking);
  let paid = paidByIndex[idx] || 0;
  let pending = Math.max(0, share - paid);
  const bookingPaid = bookingLevelPaidTotal(booking);
  const groupPay = booking?.groupPayment;
  const groupMemberCount = Number(groupPay?.memberCount || booking?.groupMembers?.length || 0);

  // Group Razorpay receipts are merged onto every sibling row for display, but each row
  // must only count that member's allocated payment — not the full group receipt total.
  // Use groupPayment totals (receipt-aware from the API) when per-row DB amounts lag.
  if (groupPay?.isOnlineGroup && groupMemberCount > 1) {
    const groupDue = Number(groupPay.totalDue ?? 0);
    const groupPaid = Number(groupPay.totalPaid ?? 0);
    let allocatedPaid = bookingPaid;
    if (groupDue > 0 && groupPaid > 0 && share > 0) {
      allocatedPaid = Math.max(
        bookingPaid,
        Math.min(share, (groupPaid / groupDue) * share)
      );
    }
    paid = allocatedPaid;
    pending = Math.max(0, share - paid);
    if (groupPay.fullyPaid) {
      paid = share > 0 ? share : total;
      pending = 0;
    }
  } else if (bookingPaid > 0 && paid > bookingPaid + 0.02) {
    paid = bookingPaid;
    pending = Math.max(0, share - paid);
  }

  const advanceInfo = effectiveBookingAdvance({ ...booking, totalAmount: total });
  const participantAdvance = advanceInfo.advanceAmount / count;
  const advanceDue = Math.max(0, participantAdvance - paid);
  const bookingBalanceDue = Math.max(0, total - bookingPaid);

  return {
    count,
    total,
    share,
    paid,
    pending,
    participantAdvance,
    advanceDue,
    bookingBalanceDue,
    participantName: participantNameAt(booking, traveler, idx),
    isWithinAdvanceWindow: advanceInfo.isWithinAdvanceWindow
  };
}

/** Prefill manual receipt amount: pending, then advance due, then booking balance share. */
export function suggestParticipantReceiptAmount(payment, booking) {
  const pending = Number(payment?.pending ?? 0);
  if (pending > 0.02 || Math.round(pending) > 0) {
    return String(Math.round(pending));
  }
  const advanceDue = Number(payment?.advanceDue ?? 0);
  if (advanceDue > 0.02 || Math.round(advanceDue) > 0) {
    return String(Math.round(advanceDue));
  }
  const count = Math.max(1, Number(payment?.count) || 1);
  const bookingBalance = Number(payment?.bookingBalanceDue ?? 0);
  if (bookingBalance > 0.02) {
    return String(Math.round(bookingBalance / count));
  }
  const remaining = Number(booking?.remainingAmount ?? booking?.remaining_amount ?? 0);
  if (remaining > 0.02) {
    return String(Math.round(remaining / count));
  }
  return '';
}

export function isParticipantFullyPaid(booking, traveler, travelerIndex) {
  if (booking?.groupPayment?.isOnlineGroup && booking?.groupPayment?.fullyPaid) return true;

  const { share, pending, paid, bookingBalanceDue } = getParticipantPaymentState(
    booking,
    traveler,
    travelerIndex
  );
  const total = bookingFeeTotal(booking);
  const feeDue = share > 0 ? share : total;
  const bookingPaid = bookingLevelPaidTotal(booking);
  const remainingStored = Number(booking?.remainingAmount ?? booking?.remaining_amount ?? 0);

  if (pending > 0.02 || Math.round(pending) > 0) return false;
  if (bookingBalanceDue > 0.02) return false;
  if (remainingStored > 0.02) return false;
  if (total > 0 && bookingPaid + 0.02 < total) return false;

  if (feeDue <= 0 && paid <= 0 && bookingPaid <= 0) return false;
  if (feeDue <= 0) return true;

  return paid + 0.02 >= feeDue;
}

/** Row status: fully paid → confirmed; advance only → advanced_paid; else registration status. */
export function getEffectiveRegistrationStatus(booking, traveler, travelerIndex, baseStatus) {
  const base = String(baseStatus || 'pending').trim().toLowerCase();
  const locked = new Set([
    'cancelled',
    'rejected',
    'rusticated',
    'volunteer',
    'instructor',
    'camp_leader',
    'photographer',
    'blogger',
    'cancellation_request'
  ]);
  if (locked.has(base)) return base;

  const { share, paid, pending } = getParticipantPaymentState(booking, traveler, travelerIndex);
  const effectiveShare = share > 0 ? share : bookingFeeTotal(booking);
  const hasRemainingBalance = pending > 0.02 || Math.round(pending) > 0;

  if (
    booking?.groupPayment?.fullyPaid ||
    (effectiveShare > 0 && paid > 0 && !hasRemainingBalance)
  ) {
    return 'confirmed';
  }

  // Advance paid (or stale DB "confirmed" with balance still due) → Advanced_Paid.
  if (paid > 0.02 && hasRemainingBalance) {
    return 'advanced_paid';
  }

  if (base === 'confirmed') return 'confirmed';
  if (base === 'advanced_paid') return 'advanced_paid';

  return base || 'pending';
}
